<template>
  <div class="personnel-data-table">
    <!-- Header Card -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-account-group</v-icon>
        <span>ข้อมูลพนักงานราชการ</span>
        <v-spacer />
        <v-chip color="white" text-color="primary" class="font-weight-bold">
          <v-icon left small>mdi-account-multiple</v-icon>
          {{ safeItemsLength }} คน
        </v-chip>
      </v-card-title>

      <!-- Search and Filter Section -->
      <v-card-text>
        <v-row>
          <!-- Search Box -->
          <v-col cols="12" md="8">
            <v-text-field
              :value="search"
              @input="$emit('update:search', $event)"
              append-icon="mdi-magnify"
              label="ค้นหา ชื่อ, เลขบัตรประชาชน, ตำแหน่ง, สาขาวิชา..."
              placeholder="พิมพ์คำค้นหา..."
              outlined
              dense
              clearable
              hide-details="auto"
              class="search-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-database-search</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <!-- Column Selector -->
          <v-col cols="12" md="4">
            <v-select
              :value="selectedHeaders"
              @input="$emit('update:selectedHeaders', $event)"
              :items="allHeaders"
              label="เลือกคอลัมน์ที่แสดง"
              multiple
              outlined
              dense
              return-object
              hide-details="auto"
              class="column-selector"
            >
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-view-column</v-icon>
              </template>
              
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 3"
                  small
                  color="primary"
                  text-color="white"
                  class="mr-1"
                >
                  {{ item.text }}
                </v-chip>
                <span
                  v-if="index === 3"
                  class="grey--text text-caption"
                >
                  (+{{ selectedHeaders.length - 3 }} อื่นๆ)
                </span>
              </template>

              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-action>
                    <v-checkbox :input-value="attrs.inputValue" />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon v-if="item.icon">
                    <v-icon small>{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <!-- Quick Filter Chips -->
        <v-row v-if="quickFilters.length > 0" class="mt-2">
          <v-col cols="12">
            <div class="d-flex align-center flex-wrap">
              <span class="text-caption grey--text mr-2">กรองด่วน:</span>
              <v-chip
                v-for="filter in quickFilters"
                :key="filter.value"
                small
                outlined
                class="mr-2 mb-2"
                @click="applyQuickFilter(filter.value)"
              >
                <v-icon left small>{{ filter.icon }}</v-icon>
                {{ filter.text }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-card elevation="2">
      <v-data-table
        :headers="displayHeaders"
        :items="safeItems"
        :loading="loading"
        :search="search"
        class="elevation-0 modern-table"
        disable-pagination
        :hide-default-footer="safeItemsLength <= 10"
        :group-by="shouldEnableGrouping ? 'position_name' : null"
        :group-desc="false"
      >
        <!-- Loading -->
        <template v-slot:progress>
          <v-progress-linear
            color="primary"
            indeterminate
            height="4"
          />
        </template>

        <!-- Group Header -->
        <template v-slot:group.header="{ items, isOpen, toggle, group }">
          <th :colspan="displayHeaders.length" class="group-header">
            <div class="d-flex align-center py-2">
              <v-btn
                icon
                small
                @click="toggle"
                class="mr-2"
              >
                <v-icon color="primary">
                  {{ isOpen ? "mdi-chevron-down" : "mdi-chevron-right" }}
                </v-icon>
              </v-btn>
              
              <v-icon color="primary" class="mr-2">mdi-briefcase</v-icon>
              
              <span class="font-weight-bold text-h6 primary--text">
                {{ group || 'ไม่ระบุตำแหน่ง' }}
              </span>
              
              <v-spacer />
              
              <v-chip
                small
                color="primary"
                text-color="white"
              >
                <v-icon left x-small>mdi-account</v-icon>
                {{ items.length }} คน
              </v-chip>
            </div>
          </th>
        </template>

        <!-- ปุ่มดำเนินการ -->
        <template v-slot:item.actions_update="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="success"
                icon
                v-bind="attrs"
                v-on="on"
                :to="`/college/gov_em/gov_emid/?id=${item.id_card}`"
              >
                <v-icon>mdi-pencil-box</v-icon>
              </v-btn>
            </template>
            <span>แก้ไขข้อมูลทั้งหมด</span>
          </v-tooltip>
        </template>

        <!-- ดูประวัติ -->
        <template v-slot:item.viewId="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :href="`#/user/resume/?id_card=${item.id_card}`"
                target="_blank"
                color="info"
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-file-document</v-icon>
              </v-btn>
            </template>
            <span>ดูประวัติส่วนตัว</span>
          </v-tooltip>
        </template>

        <!-- สัญญาจ้าง -->
        <template v-slot:item.govem_contract="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="hasContract(item.govem_contract)"
                @click="$emit('view-contract', item.govem_contract)"
                color="info"
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>
              <v-btn
                v-else
                color="error"
                icon
                disabled
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-alert-circle</v-icon>
              </v-btn>
            </template>
            <span>{{ hasContract(item.govem_contract) ? 'ดูสัญญาจ้าง' : 'ยังไม่มีสัญญา' }}</span>
          </v-tooltip>
        </template>

        <!-- แก้ไข -->
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="warning"
                icon
                v-bind="attrs"
                v-on="on"
                @click="$emit('edit-personnel', item.id_card)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>แก้ไขข้อมูลด่วน</span>
          </v-tooltip>
        </template>

        <!-- แจ้งปรับปรุง -->
        <template v-slot:item.actionNotices="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="error"
                icon
                v-bind="attrs"
                v-on="on"
                @click="$emit('notify-update', item.id_card)"
              >
                <v-icon>mdi-bell-alert</v-icon>
              </v-btn>
            </template>
            <span>แจ้งปรับปรุงข้อมูล</span>
          </v-tooltip>
        </template>

        <!-- ชื่อ-นามสกุล -->
        <template v-slot:item.frist_names="{ item }">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="32" class="mr-2">
              <span class="white--text text-caption">
                {{ getInitials(item.frist_names) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.frist_names }}</div>
              <div class="text-caption grey--text">{{ item.id_card }}</div>
            </div>
          </div>
        </template>

        <!-- ตำแหน่ง -->
        <template v-slot:item.name_postion="{ item }">
          <v-chip
            small
            color="primary"
            outlined
          >
            {{ item.name_postion || 'ไม่ระบุ' }}
          </v-chip>
        </template>

        <!-- เลขที่ตำแหน่ง -->
        <template v-slot:item.id_position="{ item }">
          <span class="font-weight-bold primary--text">
            {{ item.id_position || '-' }}
          </span>
        </template>

        <!-- ประเภท -->
        <template v-slot:item.type_position="{ item }">
          <v-chip
            x-small
            :color="getTypeColor(item.type_position)"
            text-color="white"
          >
            {{ item.type_position || 'ไม่ระบุ' }}
          </v-chip>
        </template>

        <!-- วุฒิการศึกษา -->
        <template v-slot:item.ed_abb="{ item }">
          <div class="d-flex align-center">
            <v-icon small color="info" class="mr-1">mdi-school</v-icon>
            <span>{{ item.ed_abb || '-' }}</span>
          </div>
        </template>

        <!-- สาขาวิชา -->
        <template v-slot:item.ed_name="{ item }">
          <div class="text-truncate" style="max-width: 200px;">
            {{ item.ed_name || '-' }}
          </div>
        </template>

        <!-- วันเดือนปีเกิด -->
        <template v-slot:item.brith_day="{ item }">
          <div class="d-flex align-center">
            <v-icon small color="grey" class="mr-1">mdi-cake-variant</v-icon>
            <span class="text-caption">{{ item.brith_day || '-' }}</span>
          </div>
        </template>

        <!-- วันเดือนปีบรรจุ -->
        <template v-slot:item.date_app2="{ item }">
          <div class="d-flex align-center">
            <v-icon small color="success" class="mr-1">mdi-calendar-check</v-icon>
            <span class="text-caption">{{ item.date_app2 || '-' }}</span>
          </div>
        </template>

        <!-- คำสั่งที่ -->
        <template v-slot:item.detail_order="{ item }">
          <v-chip
            x-small
            outlined
            color="info"
          >
            <v-icon left x-small>mdi-file-document-outline</v-icon>
            {{ item.detail_order || '-' }}
          </v-chip>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-account-off</v-icon>
            <div class="text-h6 grey--text mt-4">ไม่มีข้อมูลพนักงาน</div>
            <div class="text-caption grey--text">กรุณาเพิ่มข้อมูลพนักงานใหม่</div>
          </div>
        </template>

        <!-- No Results -->
        <template v-slot:no-results>
          <div class="text-center py-8">
            <v-icon size="48" color="warning">mdi-magnify-close</v-icon>
            <div class="text-subtitle-1 mt-2">ไม่พบผลการค้นหา "{{ search }}"</div>
            <div class="text-caption grey--text">
              ลองค้นหาด้วยคำอื่น หรือตรวจสอบการสะกดคำ
            </div>
            <v-btn
              small
              text
              color="primary"
              class="mt-2"
              @click="$emit('update:search', '')"
            >
              <v-icon left small>mdi-close</v-icon>
              ล้างการค้นหา
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Table Footer Info -->
      <v-divider />
      <v-card-actions class="grey lighten-4">
        <v-icon small color="grey darken-1" class="mr-1">mdi-information</v-icon>
        <span class="text-caption grey--text text--darken-1">
          แสดง {{ filteredItemsCount }} จากทั้งหมด {{ safeItemsLength }} รายการ
        </span>
        <v-spacer />
        <span class="text-caption grey--text text--darken-1">
          อัปเดตล่าสุด: {{ lastUpdated }}
        </span>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "PersonnelDataTable",
  
  props: {
    items: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: ""
    },
    selectedHeaders: {
      type: Array,
      required: true
    },
    allHeaders: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      quickFilters: [
        { text: 'ครู', value: 'ครู', icon: 'mdi-account' },
        { text: 'ผู้บริหาร', value: 'ผู้บริหาร', icon: 'mdi-account-tie' },
        { text: 'มีสัญญา', value: 'contract', icon: 'mdi-file-check' },
        { text: 'ไม่มีสัญญา', value: 'no-contract', icon: 'mdi-file-remove' }
      ]
    };
  },

  computed: {
    // ✅ แก้ไข: ตรวจสอบให้แน่ใจว่า items เป็น Array เสมอ
    safeItems() {
      if (!this.items || !Array.isArray(this.items)) {
        return [];
      }
      // กรอง items ที่มีข้อมูลครบถ้วน
      return this.items.filter(item => item && typeof item === 'object');
    },

    safeItemsLength() {
      return this.safeItems.length;
    },

    // ✅ แก้ไข: เปิดใช้งาน grouping เฉพาะเมื่อมีข้อมูลและ name_postion มีอยู่
    shouldEnableGrouping() {
      if (this.safeItemsLength === 0) return false;
      // ตรวจสอบว่ามี name_postion อยู่ใน items หรือไม่
      return this.safeItems.some(item => item.name_postion);
    },

    displayHeaders() {
      return this.headers.filter(h => this.selectedHeaders.includes(h));
    },

    filteredItemsCount() {
      if (!this.search) return this.safeItemsLength;
      return this.safeItems.filter(item => 
        JSON.stringify(item).toLowerCase().includes(this.search.toLowerCase())
      ).length;
    },

    lastUpdated() {
      const now = new Date();
      return now.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },

  methods: {
    hasContract(contract) {
      return contract && String(contract).length > 10;
    },

    getInitials(name) {
      if (!name) return '?';
      const words = name.trim().split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getTypeColor(type) {
      const colors = {
        'ครู': 'blue',
        'ผู้บริหาร': 'primary',
        'เจ้าหน้าที่': 'teal',
        'default': 'grey'
      };
      return colors[type] || colors.default;
    },

    applyQuickFilter(filterValue) {
      this.$emit('update:search', filterValue);
    }
  }
};
</script>

<style scoped>
/* เหมือนเดิม... */
.personnel-data-table {
  margin-top: 24px;
}

.search-field >>> .v-input__prepend-inner {
  margin-top: 8px;
  margin-right: 8px;
}

.search-field >>> input {
  font-size: 14px;
}

.column-selector >>> .v-input__prepend-inner {
  margin-top: 8px;
  margin-right: 8px;
}

.modern-table {
  border-radius: 4px;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 4px;
}

.modern-table >>> thead {
  background-color: #f5f5f5;
}

.modern-table >>> thead th {
  font-weight: 600 !important;
  font-size: 13px !important;
  color: #424242 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  padding: 12px 16px !important;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.modern-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 14px;
}

.group-header {
  background: var(--hv-header-navy) !important;
  border: none !important;
  position: sticky;
  top: 0;
  z-index: 2;
}

.group-header > div {
  color: white;
}

.modern-table >>> .v-btn {
  transition: all 0.2s ease;
}

.modern-table >>> .v-btn:hover {
  transform: scale(1.1);
}

.modern-table >>> .v-chip {
  font-weight: 500;
  transition: all 0.2s ease;
}

.modern-table >>> .v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.modern-table >>> .v-avatar {
  font-weight: 600;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modern-table >>> .v-data-table__progress {
  height: 4px !important;
}

.modern-table >>> .v-data-table__empty-wrapper {
  padding: 48px 24px !important;
}

.v-card {
  border-radius: 8px !important;
  overflow: hidden;
}

.v-card__title {
  font-size: 18px !important;
  padding: 16px 24px !important;
}

.v-card__text {
  padding: 16px 24px !important;
}

.v-chip.v-chip--outlined:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-color: #455a64 !important;
  cursor: pointer;
}

@media (max-width: 960px) {
  .modern-table >>> tbody td {
    padding: 8px 12px !important;
    font-size: 13px;
  }

  .modern-table >>> thead th {
    padding: 8px 12px !important;
    font-size: 12px !important;
  }

  .v-card__title {
    font-size: 16px !important;
    padding: 12px 16px !important;
  }
}

@media (max-width: 600px) {
  .group-header > div {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .group-header .v-chip {
    margin-top: 8px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modern-table >>> tbody tr {
  animation: fadeIn 0.3s ease;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.modern-table >>> tbody tr.v-data-table__selected {
  background-color: #f5f5f5 !important;
}

.v-icon {
  transition: all 0.2s ease;
}

.v-btn:hover .v-icon {
  transform: scale(1.1);
}

.v-card__actions {
  padding: 8px 16px !important;
  min-height: 48px;
}

.status-active {
  color: #2e7d32;
}

.status-inactive {
  color: #f44336;
}

.status-pending {
  color: #a86400;
}
</style>